import { useState } from 'react';
import { App as AntApp, Button, Form, Input, Modal, Select, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useCounterparties,
  useCreateCounterparty,
  type CounterpartyType,
} from '../api/cash';
import { asApiError } from '@/shared/lib/api';

// 'customer' is created automatically from CRM customers — not manually selectable.
const TYPES: CounterpartyType[] = ['employee', 'supplier', 'partner', 'other'];

export function CounterpartySelect({
  value,
  onChange,
}: {
  value?: string;
  onChange?: (id: string | undefined) => void;
}) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [search, setSearch] = useState('');
  const { data: list, isLoading } = useCounterparties({ search: search.length >= 2 ? search : undefined });
  const create = useCreateCounterparty();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  async function quickCreate() {
    try {
      const v = await form.validateFields();
      const created = await create.mutateAsync(v);
      onChange?.(created.id);
      void message.success(t('kassa.counterpartyCreated'));
      setOpen(false);
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  return (
    <>
      <Space.Compact style={{ width: '100%' }}>
        <Select
          style={{ width: '100%' }}
          showSearch
          allowClear
          value={value}
          onChange={(v) => onChange?.(v)}
          filterOption={false}
          onSearch={setSearch}
          loading={isLoading}
          placeholder={t('kassa.counterpartyPlaceholder')}
          options={(list ?? []).map((c) => ({
            value: c.id,
            label: `${c.name}${c.phone ? ` · ${c.phone}` : ''} · ${t(`kassa.counterpartyTypes.${c.type}`)}`,
          }))}
          notFoundContent={search.length < 2 ? t('kassa.counterpartyTypeHint') : t('kassa.counterpartyNotFound')}
        />
        <Button icon={<PlusOutlined />} onClick={() => setOpen(true)} />
      </Space.Compact>

      <Modal
        title={t('kassa.newCounterparty')}
        open={open}
        onOk={quickCreate}
        onCancel={() => { setOpen(false); form.resetFields(); }}
        confirmLoading={create.isPending}
        okText={t('common.create')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false} initialValues={{ type: 'other' }}>
          <Form.Item name="name" label={t('kassa.counterpartyName')} rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="type" label={t('kassa.counterpartyType')}>
            <Select options={TYPES.map((tp) => ({ value: tp, label: t(`kassa.counterpartyTypes.${tp}`) }))} />
          </Form.Item>
          <Form.Item name="phone" label={t('customers.phone')}>
            <Input placeholder="+998..." />
          </Form.Item>
          <Form.Item name="note" label={t('kassa.note')}>
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
